import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const parse = (str) => new Date(str + "T00:00:00");
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6; // Sun/Sat

/**
 * Hook that checks whether a chosen due date is a weekend or public holiday,
 * and suggests the nearest EARLIER working day. Moving the due date earlier
 * keeps the reminder window intact, so alerts are never late.
 */
export const useHolidayChecker = (country) => {
  const [byYear, setByYear] = useState({}); // { 2026: {"2026-01-01": "New Year's Day"} }
  const loadingYears = useRef(new Set());

  const ensureYear = useCallback(async (year) => {
    if (!country || byYear[year] || loadingYears.current.has(year)) return;
    loadingYears.current.add(year);
    try {
      const res = await api.get(`/holidays?year=${year}`);
      const map = {};
      (res.data.holidays || []).forEach((h) => { map[h.date] = h.name; });
      setByYear((prev) => ({ ...prev, [year]: map }));
    } catch (e) {
      setByYear((prev) => ({ ...prev, [year]: {} }));
    } finally {
      loadingYears.current.delete(year);
    }
  }, [country, byYear]);

  const holidayName = useCallback((dateStr) => {
    const y = parse(dateStr).getFullYear();
    return byYear[y]?.[dateStr] || null;
  }, [byYear]);

  /**
   * Returns null if the date is a normal working day, otherwise:
   * { type: "holiday"|"weekend", label, suggestion: "YYYY-MM-DD"|null, suggestionReady: bool }
   */
  const check = useCallback((dateStr) => {
    if (!dateStr) return null;
    const d = parse(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const weekend = isWeekend(d);
    const hName = holidayName(dateStr);
    if (!weekend && !hName) return null;

    // Walk backwards to the nearest earlier working day (skip weekends + holidays).
    let cur = new Date(d);
    let steps = 0;
    let ready = true;
    do {
      cur.setDate(cur.getDate() - 1);
      steps += 1;
      const iso = toISO(cur);
      const curYear = cur.getFullYear();
      if (country && byYear[curYear] === undefined) { ready = false; break; }
      if (!isWeekend(cur) && !holidayName(iso)) break;
    } while (steps < 14);

    return {
      type: hName ? "holiday" : "weekend",
      label: hName || (d.getDay() === 6 ? "a Saturday" : "a Sunday"),
      suggestion: ready ? toISO(cur) : null,
      suggestionReady: ready,
    };
  }, [holidayName, byYear, country]);

  return { ensureYear, check, holidayName, enabled: Boolean(country) };
};

export { toISO, isWeekend };
